import { useMemo, type FC, type HTMLAttributes } from 'react';

import CollapsableTableItem from '@Components/CollapsableTableItem';
import styles from './DisposalPriceTable.module.css';

import type { WithoutLayoutData } from '@Api/Order/getDeliveryInfo';

export interface DisposalPriceTableProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  tableData: WithoutLayoutData;
  showMobileVariant?: boolean;
  showDesktopVariant?: boolean;
}

const DisposalPriceTable: FC<DisposalPriceTableProps> = (props) => {
  const { className, tableData, showMobileVariant, showDesktopVariant, ...restProps } = props;

  const splitedTables: WithoutLayoutData[] = useMemo(() => {
    if (!tableData.body.length) return [];

    if (showDesktopVariant) return [tableData];
    const tableCenter = Math.ceil(tableData.body.length / 2);

    const firstTable = {
      header: tableData.header,
      body: tableData.body.slice(0, tableCenter),
    };

    const secondTable = {
      header: tableData.header,
      body: tableData.body.slice(tableCenter),
    };

    return [firstTable, secondTable];
  }, [tableData, showDesktopVariant]);

  const [leftTable, rightTable] = splitedTables;

  const mobileTable = useMemo(() => {
    const table = {
      group: tableData.expandedText,
      rows: tableData.body.map((item) => ({
        term: item.productTypeGroupName,
        price: item.price[0],
      })),
    };

    return table;
  }, [tableData]);

  return (
    <div {...restProps} className={className}>
      {!showMobileVariant ? (
        <table className={styles.tableContainer}>
          <thead className={styles.tableHead}>
            <tr>
              {leftTable.header.map((header) => (
                <td
                  key={header.name}
                  rowSpan={header.rowspan}
                  colSpan={header.colspan}
                  className={styles.tableDataCell}
                >
                  {header.name}
                </td>
              ))}
              {rightTable && <td className={styles.tableGap}></td>}
              {rightTable?.header.map((header) => (
                <td
                  key={header.name}
                  rowSpan={header.rowspan}
                  colSpan={header.colspan}
                  className={styles.tableDataCell}
                >
                  {header.name}
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            {leftTable.body.map((row, idx) => {
              return (
                <tr key={row.productTypeGroupName}>
                  <td className={styles.tableDataCell}>{row.productTypeGroupName}</td>
                  <td className={styles.tableDataCell}>{row.price[0]}</td>

                  {rightTable && rightTable.body[idx] && (
                    <>
                      <td className={styles.tableGap}></td>
                      <td className={styles.tableDataCell}>
                        {rightTable?.body[idx]?.productTypeGroupName}
                      </td>
                      <td className={styles.tableDataCell}>{rightTable?.body[idx]?.price[0]}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div className={styles.listContainer}>
          <CollapsableTableItem item={mobileTable} />
        </div>
      )}
    </div>
  );
};

export default DisposalPriceTable;
